import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getTimeSlots, 
  submitRegistration, 
  checkExistingRegistrations,
  mergeCanonicalTimeSlots,
  findMergedSlotBySelection,
  isTimeSlotAvailable
} from '../utils/sheets-api';
import { SHEETS_CONFIG } from '../config/sheets-config';
import {
  CALIFORNIA_REGISTRATION_DESCRIPTION,
  DALLAS_REGISTRATION_DESCRIPTION,
  getMasonRegistrationDescription,
  REGISTRATION_EVENTS,
  resolveRegistrationEventKey
} from '../config/event-registration-config';
import { FormattedEventDescription } from '../utils/format-event-description';
import './EventRegistration.css';

const emptyForm = {
  child_name: '',
  age: '',
  swimming_level: '',
  goals: '',
  phone: '',
  email: '',
  location: '',
  additional_info: ''
};

const EventRegistration = () => {
  const navigate = useNavigate();
  const { eventId: routeEventParam } = useParams();
  const eventKey = resolveRegistrationEventKey(routeEventParam);
  const regConfig = REGISTRATION_EVENTS[eventKey];
  const sheetsEventId = regConfig.sheetsEventId;
  const isDallasEvent = eventKey.startsWith('dallas-');
  const isMasonEvent = eventKey.startsWith('mason-');
  const isCaliforniaEvent = eventKey.startsWith('california-');
  const usesCompactEventDetails = isDallasEvent || isMasonEvent || isCaliforniaEvent;
  
  // State for form data
  const [formData, setFormData] = useState({
    child_name: '',
    age: '',
    swimming_level: '',
    goals: '',
    phone: '',
    email: '',
    location: '',
    additional_info: ''
  });

  // State for time slots and event
  const [timeSlots, setTimeSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [existingRegistrations, setExistingRegistrations] = useState(0);
  const [sheetsConfigured, setSheetsConfigured] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // State for form handling
  const [selectedSlots, setSelectedSlots] = useState([]);
  const selectedSlotsRef = useRef(selectedSlots);
  selectedSlotsRef.current = selectedSlots;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [phoneValidationMessage, setPhoneValidationMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [reservedSlots, setReservedSlots] = useState(new Set()); // Track temporarily reserved slots

  const eventDetails = {
    name: regConfig.name,
    date: regConfig.date,
    location: regConfig.location,
    description: isDallasEvent
      ? DALLAS_REGISTRATION_DESCRIPTION
      : isMasonEvent
        ? getMasonRegistrationDescription(regConfig.location)
        : isCaliforniaEvent
          ? CALIFORNIA_REGISTRATION_DESCRIPTION
          : regConfig.description,
    age_groups: regConfig.age_groups,
    status: regConfig.status
  };

  const fetchTimeSlots = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('=== fetchTimeSlots called ===', sheetsEventId);

      if (SHEETS_CONFIG.SCRIPT_URL === 'YOUR_SCRIPT_URL') {
        console.log('Using static data - Google Sheets not configured');
        setTimeSlots(regConfig.staticTimeSlots);
        setSheetsConfigured(false);
        setLastUpdated(new Date());
        setSubmitMessage('⚠️ Google Sheets not configured yet. Using demo data. Please follow the setup guide to enable real registrations.');
      } else {
        console.log('Attempting to fetch from Google Sheets...');
        const slots = await getTimeSlots(sheetsEventId);

        if (slots && Array.isArray(slots)) {
          const mergedSlots = mergeCanonicalTimeSlots(regConfig.staticTimeSlots, slots);
          setTimeSlots(mergedSlots);
          setSheetsConfigured(true);
          setSubmitMessage(
            slots.length === 0
              ? 'Showing default time slots. Add rows for this event ID in your TimeSlots sheet to sync live capacity.'
              : ''
          );
          setLastUpdated(new Date());
        } else {
          throw new Error('Invalid time slots data received from Google Sheets');
        }
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setTimeSlots(regConfig.staticTimeSlots);
      setSheetsConfigured(false);
      setLastUpdated(new Date());
      setSubmitMessage('⚠️ Unable to load time slots from Google Sheets. Using demo data. Please check your setup.');
    } finally {
      setIsLoading(false);
      console.log('=== fetchTimeSlots completed ===');
    }
  }, [sheetsEventId, regConfig.staticTimeSlots]);

  const testGoogleScriptConnection = async () => {
    if (SHEETS_CONFIG.SCRIPT_URL === 'YOUR_SCRIPT_URL') {
      console.log('Google Sheets not configured - skipping connection test');
      return;
    }

    try {
      console.log('Testing Google Apps Script connectivity...');
      const testUrl = `${SHEETS_CONFIG.SCRIPT_URL}?action=test&t=${new Date().getTime()}`;
      const response = await fetch(testUrl, { method: 'GET' });

      if (response.ok) {
        console.log('✅ Google Apps Script is accessible');
      } else {
        console.warn('⚠️ Google Apps Script returned status:', response.status);
      }
    } catch (error) {
      console.error('❌ Google Apps Script connection test failed:', error);
    }
  };

  // Reset form and load slots when switching events
  useEffect(() => {
    setFormData({ ...emptyForm });
    setSelectedSlots([]);
    setReservedSlots(new Set());
    setExistingRegistrations(0);
    setSubmitMessage('');
    setPhoneValidationMessage('');
    setShowConfirmation(false);
    setRegistrationDetails(null);
  }, [eventKey]);

  useEffect(() => {
    fetchTimeSlots();
    testGoogleScriptConnection();
  }, [fetchTimeSlots]);

  // Cleanup: release all reservations when component unmounts
  useEffect(() => {
    return () => {
      selectedSlotsRef.current.forEach(slot => releaseTimeSlot(slot.id));
    };
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate phone number in real-time
    if (name === 'phone') {
      validatePhoneNumber(value);
    }

    // Check existing registrations when email or phone changes (only if Google Sheets is configured)
    if (sheetsConfigured && (name === 'email' || name === 'phone')) {
      checkExistingRegistrationsForUser(value, formData[name === 'email' ? 'phone' : 'email']);
    }
  };

  // Validate phone number format
  const validatePhoneNumber = (phoneNumber) => {
    const phonePattern = /^\d{10}$/;
    
    if (phoneNumber && !phonePattern.test(phoneNumber)) {
      setPhoneValidationMessage('⚠️ Please enter phone number with exactly 10 digits (e.g., 5551234567)');
    } else if (phoneNumber && phonePattern.test(phoneNumber)) {
      setPhoneValidationMessage(''); // Clear any previous phone format warnings
    }
  };

  // Check existing registrations for a user
  const checkExistingRegistrationsForUser = async (email, phone) => {
    if (!email || !phone || !sheetsConfigured) return;
    
    try {
      const count = await checkExistingRegistrations(email, phone, sheetsEventId);
      setExistingRegistrations(count);
    } catch (error) {
      console.error('Error checking existing registrations:', error);
    }
  };

  // Handle time slot selection
  const handleSlotSelect = (slot) => {
    if (isSlotDisabled(slot)) {
      return; // Slot is not available
    }

    // Check if slot is already reserved by another user
    if (reservedSlots.has(slot.id) && !selectedSlots.includes(slot)) {
      setSubmitMessage('⚠️ This time slot is currently being registered by another user. Please try again in a moment.');
      return;
    }

    // If selecting the same slot, deselect it
    if (selectedSlots.includes(slot)) {
      setSelectedSlots([]);
      releaseTimeSlot(slot.id);
      console.log('Time slot deselected:', slot.time);
      return;
    }

    // Release previous selection if any
    if (selectedSlots.length > 0) {
      releaseTimeSlot(selectedSlots[0].id);
    }

    // Allow changing selection - replace current selection with new one
    setSelectedSlots([slot]);
    
    // Reserve the slot temporarily (5 minutes)
    reserveTimeSlot(slot.id);
    
    console.log('Time slot selected and reserved:', slot.time);
  };

  // Check if a slot is disabled
  const isSlotDisabled = (slot) => {
    // Slot is disabled if not available OR if reserved by another user (but not by current user)
    return !isTimeSlotAvailable(slot) || (reservedSlots.has(slot.id) && !selectedSlots.includes(slot));
  };

  // Reserve a time slot temporarily
  const reserveTimeSlot = (slotId) => {
    setReservedSlots(prev => new Set([...prev, slotId]));
    
    // Auto-release reservation after 1 minute
    setTimeout(() => {
      setReservedSlots(prev => {
        const newSet = new Set(prev);
        newSet.delete(slotId);
        return newSet;
      });
    }, 1 * 60 * 1000); // 1 minute
  };

  // Release a time slot reservation
  const releaseTimeSlot = (slotId) => {
    setReservedSlots(prev => {
      const newSet = new Set(prev);
      newSet.delete(slotId);
      return newSet;
    });
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.child_name.trim()) {
      setSubmitMessage('Please enter the child\'s name.');
      return false;
    }

    if (!formData.age.trim()) {
      setSubmitMessage('Please enter the child\'s age.');
      return false;
    }

    if (!formData.swimming_level.trim()) {
      setSubmitMessage('Please select a swimming level.');
      return false;
    }

    if (!formData.goals.trim()) {
      setSubmitMessage('Please enter goals for swimming lessons.');
      return false;
    }

    if (!formData.phone.trim()) {
      setSubmitMessage('Please enter a phone number.');
      return false;
    }

    // Validate phone number format
    const phonePattern = /^\d{10}$/;
    if (!phonePattern.test(formData.phone.trim())) {
      setSubmitMessage('⚠️ Please enter phone number with exactly 10 digits (e.g., 5551234567)');
      return false;
    }

    if (!formData.email.trim()) {
      setSubmitMessage('Please enter an email address.');
      return false;
    }

    if (selectedSlots.length === 0) {
      setSubmitMessage('Please select a time slot.');
      return false;
    }

    if (sheetsConfigured && existingRegistrations >= SHEETS_CONFIG.MAX_REGISTRATIONS_PER_PERSON) {
      setSubmitMessage('You have reached the maximum of 2 registrations per person.');
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      if (!sheetsConfigured) {
        // Demo mode - show success message but don't actually submit
        setSubmitMessage('🎉 Demo registration submitted! This is a preview. Please set up Google Sheets to enable real registrations.');
        handleReset();
        return;
      }

      // Final availability check — use merged slots (same as UI), not raw sheet ids
      const selectedSlot = selectedSlots[0];
      const rawSlots = await getTimeSlots(sheetsEventId);
      const currentSlot = findMergedSlotBySelection(
        regConfig.staticTimeSlots,
        rawSlots,
        selectedSlot
      );

      if (!currentSlot || !isTimeSlotAvailable(currentSlot)) {
        setSubmitMessage(
          '⚠️ Sorry, this time slot is no longer available. Please select another time slot.'
        );
        releaseTimeSlot(selectedSlot.id);
        setSelectedSlots([]);
        setIsSubmitting(false);
        return;
      }

      const slot = selectedSlots[0];
      const weekday = slot.day || '';
      const registrationData = {
        ...formData,
        eventId: sheetsEventId,
        timeSlot: slot.time,
        time_slot_weekday: weekday,
        event_datetime_summary: `${regConfig.name} — ${regConfig.date}${weekday ? ` (${weekday})` : ''} — ${slot.time}`,
        event_summary_name: regConfig.name,
        event_summary_date: regConfig.date,
        event_summary_location: regConfig.location,
        event_summary_description: regConfig.description,
        eventName: regConfig.name,
        eventDate: regConfig.date,
        eventLocation: regConfig.location,
        eventDescription: regConfig.description
      };

      const response = await submitRegistration(registrationData);
      
      if (response.success) {
        // Release the reservation
        releaseTimeSlot(selectedSlots[0].id);
        
        // Store registration details for confirmation popup
        setRegistrationDetails({
          childName: formData.child_name,
          timeSlot: selectedSlots[0].time,
          registrationId: response.registrationId
        });
        
        // Show confirmation popup
        setShowConfirmation(true);
        
        // Refresh time slots to show updated availability
        console.log('Registration successful, refreshing time slots...');
        await fetchTimeSlots();
        
      } else {
        setSubmitMessage(response.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting registration:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Provide more specific error messages
      if (error.message.includes('Failed to fetch')) {
        setSubmitMessage('⚠️ Network error: Unable to connect to the registration system. Please check your internet connection and try again.');
      } else if (error.message.includes('HTTP error')) {
        setSubmitMessage('⚠️ Server error: The registration system is temporarily unavailable. Please try again in a few minutes.');
      } else if (error.message.includes('Failed to submit registration')) {
        setSubmitMessage('⚠️ Registration failed: ' + (error.message.replace('Failed to submit registration: ', '') || 'Please try again.'));
      } else {
        setSubmitMessage('⚠️ Registration error: ' + (error.message || 'Please try again or contact us directly.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const handleReset = () => {
    // Release any reserved time slots
    selectedSlots.forEach(slot => releaseTimeSlot(slot.id));
    
    setFormData({ ...emptyForm });
    setSelectedSlots([]);
    setSubmitMessage('');
    setPhoneValidationMessage('');
    setExistingRegistrations(0);
  };

  // Handle confirmation popup close
  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setRegistrationDetails(null);
    setPhoneValidationMessage(''); // Clear phone validation message
    handleReset(); // Reset form to return to registration page
  };

  if (isLoading) {
    return (
      <div className="event-registration">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading time slots...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="event-registration">
      <div className="container">
        {/* Header Section */}
        <div className="registration-header">
          <div className="header-content">
            <div className="logo">
              <h1>Swim With a Splash</h1>
            </div>
            <nav className="nav-menu">
              <button onClick={() => navigate('/')} className="nav-btn">Home</button>
              <button onClick={() => navigate('/events')} className="nav-btn">Events</button>
              <button onClick={() => navigate('/feedback')} className="nav-btn">Feedback</button>
            </nav>
          </div>
        </div>

        <div className="event-details">
          <h2>{eventDetails.name}</h2>
          {usesCompactEventDetails ? (
            <FormattedEventDescription text={eventDetails.description} />
          ) : (
            <>
              <p>
                <strong>Date:</strong> {eventDetails.date}
              </p>
              {eventDetails.location ? (
                <p>
                  <strong>Location:</strong> {eventDetails.location}
                </p>
              ) : null}
              <p>
                <strong>Description:</strong>{' '}
                <FormattedEventDescription text={eventDetails.description} />
              </p>
              {eventDetails.age_groups ? (
                <p>
                  <strong>Age Groups:</strong> {eventDetails.age_groups}
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="time-slots-section">
          <div className="time-slots-header">
            <h3>Select a Time Slot</h3>
            <div className="time-slots-controls">
              {lastUpdated && (
                <div className="last-updated">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </div>
              )}
              <button 
                onClick={fetchTimeSlots} 
                className="refresh-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing...' : '🔄 Refresh Availability'}
              </button>
              {selectedSlots.length > 0 && (
                <button 
                  onClick={() => setSelectedSlots([])} 
                  className="clear-selection-btn"
                  type="button"
                >
                  🗑️ Clear Selection
                </button>
              )}
            </div>
          </div>
            
          
                     <div className="time-slots-grid">
             {timeSlots.map((slot) => (
               <div
                 key={slot.id}
                 className={`time-slot ${selectedSlots.includes(slot) ? 'selected' : ''} ${isSlotDisabled(slot) ? 'disabled' : ''} ${reservedSlots.has(slot.id) ? 'reserved' : ''}`}
                 onClick={() => handleSlotSelect(slot)}
               >
                 <div className="slot-time">{slot.time}</div>
                                   <div className="slot-availability">
                    {slot.status === 'Full' ? (
                      'Full'
                    ) : slot.available_spots > 0 ? (
                      `${slot.available_spots} spot${slot.available_spots !== 1 ? 's' : ''} available`
                    ) : (
                      'Full'
                    )}
                  </div>
                 {slot.status === 'Full' && <div className="full-badge">Full</div>}
                                   {reservedSlots.has(slot.id) && !selectedSlots.includes(slot) && (
                    <div className="reserved-badge">Reserved by Another User</div>
                  )}
                  {selectedSlots.includes(slot) && (
                    <div className="selected-badge">Selected by You</div>
                  )}
               </div>
             ))}
           </div>
        </div>

        {sheetsConfigured && existingRegistrations > 0 && (
          <div className={`registration-limit-warning ${existingRegistrations >= 2 ? 'limit-reached' : 'limit-remaining'}`}>
            <strong>Registration Status:</strong> You have {existingRegistrations} registration{existingRegistrations !== 1 ? 's' : ''} for this event.
            {existingRegistrations >= 2 ? ' Maximum limit reached.' : ` You can register ${2 - existingRegistrations} more time${2 - existingRegistrations !== 1 ? 's' : ''}.`}
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-section">
            <h3>Child Information</h3>
            
            <div className="form-group">
              <label htmlFor="child_name">Child's Name *</label>
              <input
                type="text"
                id="child_name"
                name="child_name"
                value={formData.child_name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="age">Age *</label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                min="1"
                max="18"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="swimming_level">Swimming Level *</label>
              <select
                id="swimming_level"
                name="swimming_level"
                value={formData.swimming_level}
                onChange={handleInputChange}
                required
              >
                <option value="">Select swimming level</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="goals">Goals for Swimming Lessons *</label>
              <textarea
                id="goals"
                name="goals"
                value={formData.goals}
                onChange={handleInputChange}
                rows="3"
                placeholder="What would you like to achieve?"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Contact Information</h3>
            
                          <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="xxxxxxxxxx"
                  pattern="[0-9]{10}"
                  title="Please enter a valid phone number with 10 digits (xxxxxxxxxx)"
                  required
                />
                {phoneValidationMessage && (
                  <div className="phone-validation-message">
                    {phoneValidationMessage}
                  </div>
                )}
              </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="City, State"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Additional Information</h3>
            
            <div className="form-group">
              <label htmlFor="additional_info">Additional Information</label>
              <textarea
                id="additional_info"
                name="additional_info"
                value={formData.additional_info}
                onChange={handleInputChange}
                rows="3"
                placeholder="Any special needs, concerns, or additional information..."
              />
            </div>
                     </div>

           <div className="form-section">
             <div className="contact-info-notice">
               <p>For any changes to registration, please contact <strong>swimwithasplash@gmail.com</strong></p>
             </div>
           </div>

           {submitMessage && (
             <div className={`submit-message ${submitMessage.includes('successfully') || submitMessage.includes('Demo') ? 'success' : 'error'}`}>
               {submitMessage}
             </div>
           )}

           <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : sheetsConfigured ? 'Submit Registration' : 'Demo Registration'}
            </button>
            <button type="button" onClick={handleReset} className="reset-btn">
              Reset Form
            </button>
            <button type="button" onClick={() => navigate('/events')} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>

        {/* Confirmation Popup */}
        {showConfirmation && (
          <div className="confirmation-overlay">
            <div className="confirmation-popup">
              <div className="confirmation-header">
                <h2>🎉 Registration Successful!</h2>
              </div>
              <div className="confirmation-content">
                <p><strong>Child's Name:</strong> {registrationDetails?.childName}</p>
                <p><strong>Time Slot:</strong> {registrationDetails?.timeSlot}</p>
                <p><strong>Registration ID:</strong> {registrationDetails?.registrationId}</p>
                <p>You should receive a <strong>registration confirmed</strong> email at the address you provided right away. If you do not see it, check your spam folder.</p>
              </div>
              <div className="confirmation-actions">
                <button 
                  onClick={handleConfirmationClose}
                  className="confirmation-btn"
                >
                  Return to Registration Page
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventRegistration; 